import { injectAdaptiveFavicons, pruneHibidArchivedImages, markPortalReady } from './bootstrap';
import { initAuth0 } from './authentication';
import type { PortalRuntimeContext } from './runtimeContext';
import { els, closeLotModal, openLotDetailsModal } from '../modules/ui';
import { setupTableHandlers } from '../eventHandlers';
import { setupUrlImportListeners } from '../modules/urlImport';
import { performLogout, redirectLogin, applyUser } from '../modules/auth';
import { loadStoredAuth, save } from '../modules/state';
import { cleanupStaleSessionStorage } from '../modules/secureAuthStorage';
import { clearSyncIndicatorDelay } from '../modules/syncManager';
import { cancelPolling, cancelCreditPolling, scheduleCreditPolling } from '../modules/polling';
import { clearCreditCache, fetchCreditBalance } from '../modules/credit';
import { setupNotificationFeed } from '../modules/notificationFeed';
import { showToast } from '../modules/ui';
import { resetRemoteSnapshot } from '../modules/lotOperations';
import { loadLocalLots } from '../modules/settingsLoader';
import { rehydratePendingPriceReviewFromStorage } from '../modules/priceReview';
import { initializeCountdownUpdates } from '../coordination';
import { beginEstimateEdit } from '../modules/estimates';
import { createLogger } from '../../shared/logger';
import { ensureAccessToken } from '../modules/api';

const log = createLogger('portal:app');

// Module-level reference to portal config - prevents minifier from shadowing
// This is intentionally at module scope so the minifier cannot reuse
// this variable name for inline function variables.
let __portalConfigRef__: PortalRuntimeContext['cfg'] | null = null;

export function createBootSequence(ctx: PortalRuntimeContext): () => void {
  log.info('[createBootSequence] Creating boot sequence');
  // Store cfg in module-level variable before destructuring
  __portalConfigRef__ = ctx.cfg;
  log.info('[createBootSequence] __portalConfigRef__ set:', { hasCfg: !!__portalConfigRef__ });

  const {
    cfg,
    state,
    render,
    startRemoteLotsFetch,
    disconnectRealTime,
    applyModeBannerForConfig,
    renderUserIdentity,
    hideAuthError,
    applyPendingProfile,
    logout,
  } = ctx;

  log.info('[createBootSequence] Destructuring complete, returning boot function');

  return function boot() {
    log.info('[boot] Boot function started');

    try {
      injectAdaptiveFavicons();
      log.info('[boot] Favicons injected');
    } catch (e) {
      log.error('[boot] Favicon injection failed:', e);
    }

    // Cleanup stale sessionStorage entries that may have been left from previous sessions
    cleanupStaleSessionStorage();

    if (els.logout) {
      els.logout.addEventListener('click', () => {
        performLogout({
          AUTH0_DOMAIN: logout.AUTH0_DOMAIN,
          AUTH0_CLIENT_ID: logout.AUTH0_CLIENT_ID,
          clearSyncIndicatorDelay,
          cancelPolling,
          cancelCreditPolling,
          clearCreditCache,
          resetRemoteSnapshot,
          disconnectRealTime,
          hideAuthError,
          render,
          applyModeBanner: applyModeBannerForConfig,
          save,
          logoutReturnUrl: logout.logoutReturnUrl,
          useAuth0Logout: logout.useAuth0Logout,
        });
      });
    }

    // Wire the sign-in buttons to start the Auth0 SPA-SDK (PKCE) login redirect.
    // Both #connect (els.login) and #hero-signin trigger the same flow.
    const startSignIn = (button: HTMLButtonElement) => {
      if (button.dataset.pending === 'true') return;
      button.dataset.pending = 'true';
      const originalText = button.textContent || 'Sign in to Lot Lingo';
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Redirecting to sign-in…';
      // Restore the button if navigation is somehow blocked (rare).
      window.setTimeout(() => {
        if (!button.isConnected) return;
        if (document.hidden) return;
        button.disabled = false;
        button.dataset.pending = 'false';
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }, 15000);
      redirectLogin();
    };

    const connectButton = els.login;
    if (connectButton instanceof HTMLButtonElement) {
      connectButton.addEventListener('click', () => startSignIn(connectButton));
    }

    const heroSignin = document.getElementById('hero-signin');
    if (heroSignin instanceof HTMLButtonElement) {
      heroSignin.addEventListener('click', () => startSignIn(heroSignin));
    }

    try {
      pruneHibidArchivedImages();
    } catch {}

    setupTableHandlers({
      state,
      els: {
        rows: els.rows,
        imagesModalContent: els.imagesModalContent,
        lotDetailsModalContent: els.lotDetailsModalContent,
        lotCardsContainer: els.lotCardsContainer,
      },
      render,
      ensureAccessToken,
      applyModeBanner: applyModeBannerForConfig,
      DEBUG: cfg?.debug || false,
    });

    const importConfirmBackdrop = document.getElementById('import-confirm-backdrop');
    const importConfirmModal = document.getElementById('import-confirm-modal');
    const importConfirmSource = document.getElementById('import-confirm-source');
    const importConfirmYes = document.getElementById('import-confirm-yes');
    const importConfirmNo = document.getElementById('import-confirm-no');

    if (!importConfirmBackdrop || !importConfirmModal || !importConfirmYes || !importConfirmNo) {
      log.warn('Confirmation dialog elements not found. Feature disabled.');
    }

    setupUrlImportListeners(
      els,
      state,
      cfg,
      closeLotModal,
      render,
      importConfirmBackdrop,
      importConfirmModal,
      importConfirmSource,
      importConfirmYes,
      importConfirmNo
    );

    const hasAuth0 = !!(cfg?.auth0Domain && cfg?.auth0ClientId);
    const storedAuth = loadStoredAuth();
    const hasStoredAuth = !!storedAuth;
    const hasValidAuth =
      storedAuth && storedAuth.accessToken && Date.now() < (storedAuth.expiresAt || 0);

    log.info('[boot] Auth state check:', {
      hasAuth0,
      hasStoredAuth,
      hasValidAuth,
      reason: !storedAuth
        ? 'auth lost on refresh (in-memory cache cleared)'
        : !storedAuth.accessToken
          ? 'missing accessToken'
          : Date.now() >= (storedAuth.expiresAt || 0)
            ? 'token expired'
            : 'valid',
    });

    if (hasValidAuth && hasAuth0 && !logout.wasLoggedOut) {
      state.mode = 'auth0';
      state.extAccessToken = storedAuth.accessToken;
      state.extRefreshToken = storedAuth.refreshToken || null;
      state.extExpiresAt = storedAuth.expiresAt || 0;
      const userCandidate = storedAuth.user;
      const isEmptyObject =
        userCandidate &&
        typeof userCandidate === 'object' &&
        Object.keys(userCandidate).length === 0;
      applyUser(isEmptyObject ? null : ((userCandidate as any) ?? null));
      renderUserIdentity();
    } else if (logout.wasLoggedOut) {
      applyUser(null);
      state.mode = 'local';
    } else if (!hasStoredAuth) {
      // Auth lost on refresh (in-memory cache cleared)
      log.info('[boot] Auth lost on refresh, will attempt recovery during auth0 init');
      applyUser(null);
      state.mode = 'local';
    }

    loadLocalLots();
    try {
      rehydratePendingPriceReviewFromStorage(state, render);
    } catch {}
    render();
    renderUserIdentity();
    applyModeBannerForConfig();

    // CRITICAL: Use module-level __portalConfigRef__ to prevent minifier shadowing.
    // The minifier aggressively reuses variable names in inline functions,
    // which can shadow both 'cfg' and 'ctx' (the context parameter).
    // Using a unique module-level variable name prevents this issue.
    log.info('[boot] About to check __portalConfigRef__', {
      hasRef: !!__portalConfigRef__,
      refType: typeof __portalConfigRef__,
    });
    if (__portalConfigRef__) {
      log.info('[boot] Calling initAuth0 with cfg');
      void initAuth0({
        state,
        cfg: __portalConfigRef__,
        applyModeBanner: applyModeBannerForConfig,
        startRemoteLotsFetch,
        scheduleCreditPolling,
        applyPendingProfile,
        cancelPolling,
        cancelCreditPolling,
        renderUserIdentity,
        hideAuthError,
        wasLoggedOut: logout.wasLoggedOut,
      });
      log.info('[boot] initAuth0 call returned (void promise)');
    } else {
      log.warn('[boot] __portalConfigRef__ is null/undefined, skipping initAuth0!');
    }

    initializeCountdownUpdates();

    // Header notification bell + feed (server-generated alerts)
    try {
      setupNotificationFeed();
    } catch (e) {
      log.warn('[boot] notification feed setup failed:', e);
    }

    // Stripe checkout return: show outcome, refresh credits, clean the URL.
    try {
      const params = new URLSearchParams(window.location.search);
      const checkoutStatus = params.get('checkout');
      if (checkoutStatus === 'success' || checkoutStatus === 'cancel') {
        if (checkoutStatus === 'success') {
          showToast('Payment received — your credits are being updated.', {
            variant: 'success',
            duration: 5000,
          });
          // Webhook delivery can lag the redirect; refresh now and shortly after.
          void fetchCreditBalance(true);
          window.setTimeout(() => void fetchCreditBalance(true), 5000);
        } else {
          showToast('Checkout canceled — no charge was made.', {
            variant: 'warning',
            duration: 4000,
          });
        }
        params.delete('checkout');
        params.delete('session_id');
        const query = params.toString();
        const cleaned =
          window.location.pathname + (query ? '?' + query : '') + window.location.hash;
        window.history.replaceState({}, document.title, cleaned);
      }
    } catch {
      /* non-fatal */
    }

    markPortalReady({
      openLotDetailsModal,
      beginEstimateEdit,
    });
  };
}

export function attachBootSequence(boot: () => void): void {
  log.info('[attachBootSequence] Called, readyState:', document.readyState);
  if (document.readyState === 'loading') {
    log.info('[attachBootSequence] Deferring to DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    log.info('[attachBootSequence] Calling boot immediately');
    boot();
  }
}
